/**
 * The tab shell's two extension slots (docs/ux/artboards/optimal decisions 5 and 7):
 *
 *  - **Inspector** — the one right-hand panel (`?i=kind:id`). A page renders its inspector
 *    through `<InspectorSlot>`; the shell places it beside the page (push at ≥1680 px, overlay
 *    below, a full-screen push on phones; the panel's own CSS decides). Only one at a time.
 *  - **Status line** — the desktop bottom line. The shell renders the location and machine load;
 *    a page adds its selection's actions and keys through `<StatusActions>`.
 *
 * Both are portals into elements the shell owns, so a page never needs to know the layout, and a
 * later package can grow either slot without touching the pages.
 */
import { createContext, type ReactNode, useContext } from 'react'
import { createPortal } from 'react-dom'

export interface ShellSlots {
  inspector: HTMLElement | null
  statusActions: HTMLElement | null
}

export const ShellSlotsContext = createContext<ShellSlots>({
  inspector: null,
  statusActions: null,
})

export const useShellSlots = () => useContext(ShellSlotsContext)

/** Renders `children` in the shell's right-hand inspector slot (in place outside the tab shell). */
export function InspectorSlot({ children }: { children: ReactNode }) {
  const { inspector } = useShellSlots()
  return inspector ? createPortal(children, inspector) : children
}

/** Renders `children` in the status line's selection area (nothing outside the tab shell). */
export function StatusActions({ children }: { children: ReactNode }) {
  const { statusActions } = useShellSlots()
  return statusActions ? createPortal(children, statusActions) : null
}
